import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Event } from '../types/database';
import { EventForm } from './EventForm';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { Database } from '../types/database';

interface ContactEventsProps {
  contactId: string;
  onEventClick?: (event: Event) => void;
  refreshTrigger?: number; // Add refresh trigger prop
  onEventDeleted?: () => void; // Add callback for when event is deleted
}

export const ContactEvents: React.FC<ContactEventsProps> = ({ contactId, onEventClick, refreshTrigger, onEventDeleted }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddEventOpen, setIsAddEventOpen] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(5);
  const supabase = useSupabaseClient<Database>();

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEvents(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [contactId, supabase]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Watch for refresh trigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined) {
      fetchEvents();
    }
  }, [refreshTrigger, fetchEvents]);

  const handleDeleteEvent = async (eventId: string) => {
    try {
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', eventId);

      if (error) throw error;
      await fetchEvents();
      onEventDeleted?.(); // Notify parent component
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete event');
    }
  };

  const displayedEvents = events.slice(0, displayLimit);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Activity</Typography>
        <Button
          variant="contained"
          onClick={() => setIsAddEventOpen(true)}
        >
          Add Activity
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : events.length === 0 ? (
        <Typography color="text.secondary">No events found</Typography>
      ) : (
        <List>
          {displayedEvents.map((event) => (
            <ListItem
              key={event.id}
              sx={{
                cursor: onEventClick ? 'pointer' : 'default',
                '&:hover': onEventClick ? {
                  bgcolor: 'action.hover',
                } : {},
              }}
              onClick={() => onEventClick?.(event)}
              secondaryAction={
                <IconButton
                  edge="end"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteEvent(event.id);
                  }}
                  sx={{ color: 'white' }}
                >
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={event.metadata?.type ? 
                        event.metadata.type.charAt(0).toUpperCase() + 
                        event.metadata.type.slice(1).replace('_', ' ') : 
                        'Unknown'} 
                      size="small" 
                    />
                    <Typography>
                      {event.title}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
          
          {events.length > displayLimit && (
            <ListItem
              onClick={() => setDisplayLimit(prev => prev + 5)}
              sx={{ 
                py: 0.5, 
                cursor: 'pointer', 
                '&:hover': { 
                  backgroundColor: 'action.hover'
                } 
              }}
            >
              <ListItemText
                primary={
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'primary.main',
                      fontStyle: 'italic',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '0.75rem'
                    }}
                  >
                    Load More
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>
      )}

      <Dialog
        open={isAddEventOpen}
        onClose={() => setIsAddEventOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Event</DialogTitle>
        <EventForm
          contactId={contactId}
          onSuccess={() => {
            setIsAddEventOpen(false);
            fetchEvents();
          }}
          onCancel={() => setIsAddEventOpen(false)}
        />
      </Dialog>
    </Box>
  );
}; 